import { useCallback, useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { useBatchId } from '../../../hooks/useBatchId';
import { CandidateListData } from '../../../shared/VolunteerApplicationServiceTypes';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import Icon from '../../../components/ui-components/Icon';
import { FUNCTIONARY_HOME } from '../../../shared/Routes';
import { useScreeningDashboard } from './useScreeningDashboard';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { FilterDashboard } from './FilterDashboard';
import ProposerIcon from '../../../assets/images/proposer-icon.svg';
import WaitingApprover from '../../../assets/images/waiting-approver.svg';
import ApprovedCandidates from '../../../assets/images/approved-candidates.svg';
import ProposerRecommendation from '../../../assets/images/proposer-recommendation.svg';
import NotGoingForward from '../../../assets/images/not-going-forward.svg';
import { UndoOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const ScreeningDashboard = () => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = i18n.language === 'ta' || i18n.language === 'ma';
	const { navigateWithBatchId } = useBatchId();
	const [dashboardData, setDashboardData] = useState<CandidateListData[]>([]);
	const { adminDashboardResponse, dashboardFilter, onDashboardFilterChange } =
		useScreeningDashboard();
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);

	useEffect(() => {
		setIsFilterApplied(!isEmpty(dashboardFilter));
	}, [dashboardFilter]);

	const handleBackFunctionaryClick = useCallback(() => {
		navigateWithBatchId(FUNCTIONARY_HOME);
	}, [navigateWithBatchId]);

	const getDashboardData = useCallback(() => {
		if (adminDashboardResponse) {
			const refinedResponseData = [
				{
					count: adminDashboardResponse.overallCandidateApplication || 0,
					description: t('overallCandidateApplications'),
					imageSrc: ProposerIcon,
				},
				{
					count: adminDashboardResponse.proposerYetToBeAssigned || 0,
					description: t('proposerYetToBeAssigned'),
					imageSrc: WaitingApprover,
				},
				{
					count: adminDashboardResponse.waitingForProposer || 0,
					description: t('waitingForProposerReview'),
					imageSrc: WaitingApprover,
				},
				{
					count: adminDashboardResponse.proposerProvidedRecommendation || 0,
					description: t('proposerProvidedRecommendation'),
					imageSrc: ProposerRecommendation,
				},
				{
					count: adminDashboardResponse.waitingForCareTeam || 0,
					description: t('waitingForCareTeamReview'),
					imageSrc: WaitingApprover,
				},
				{
					count: adminDashboardResponse.approvedCandidates || 0,
					description: t('approvedCandidates'),
					imageSrc: ApprovedCandidates,
				},
				{
					count: adminDashboardResponse.notGoingForwardCandidates || 0,
					description: t('notGoingForwardCandidates'),
					imageSrc: NotGoingForward,
				},
			];
			setDashboardData(refinedResponseData);
		}
	}, [adminDashboardResponse, t]);

	useEffect(() => {
		void getDashboardData();
	}, [getDashboardData]);

	const handleFilterPopup = () => {
		setIsFilterModalOpen(true);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const handleResetFilters = useCallback(() => {
		onDashboardFilterChange({});
	}, [onDashboardFilterChange]);

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">
					{t('adminDashboard')}
				</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackFunctionaryClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackFunctionaryClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2">
					{t('adminDashboard')}
				</Text>

				{/* Filter Button - Desktop */}
				<div
					className={`flex ${
						isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
					}`}
				>
					{isFilterApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
					<Button
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterApplied ? t('filterApplied') : t('filter')}
					</Button>
				</div>

				{/* Filter Icon - Mobile */}
				<Button
					className="block sm:hidden"
					variant="outlined"
					onClick={handleFilterPopup}
				>
					<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
				</Button>
			</div>
			<div className="flex flex-wrap gap-4 pt-2 pb-5 px-5 md:py-5 md:px-5">
				{dashboardData.map((data, index) => (
					<div
						key={index}
						className="flex-1 basis-[calc(50%-0.5rem)] max-w-[calc(50%-0.5rem)] lg:basis-[calc(25%-0.75rem)] lg:max-w-[calc(25%-0.75rem)]"
					>
						<div className="bg-[#E8E8E980] md:bg-white rounded-lg border-solid border-[#DADADA] px-2 md:px-4 py-2 h-full">
							<div className="flex gap-3 md:gap-6 items-center min-h-[100px]">
								<div className="flex-shrink-0">
									<img
										src={data.imageSrc}
										className="w-[38px] md:w-[50px] h-[38px] md:h-[50px]"
									/>
								</div>

								<div className="flex flex-col justify-center">
									<Text className="text-sm md:text-lg leading-normal font-bold m-0">
										{data.count}
									</Text>
									<Text className="text-[12px] md:text-[16px] text-gray-600 m-0">
										{data.description}
									</Text>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
			<FilterDashboard
				open={isFilterModalOpen}
				onClose={handleFilterModalClose}
				filterValue={dashboardFilter}
				onFilterChange={onDashboardFilterChange}
			/>
		</>
	);
};
